import { useState } from 'react';
import type { EstadoDeReclamo, ReclamoAdministrado, TipoDeReclamo } from '@medchoice/shared';
import { administracion } from '../../api/administracion';
import { ApiError } from '../../api/client';
import { usePedido } from '../../api/usePedido';
import { Enlace, irA, volver } from '../../router';
import type { ScreenProps } from '../../routes/types';
import {
  AreaTexto, AvisoError, Boton, Cargando, EstadoVacio, Estrellas, FalloDeCarga, Icono, Insignia, Pestanas, Tarjeta,
  useAvisos, type TonoInsignia, useTitulo,
} from '../../ui';
import { SeccionPanel } from '../../ui/layout/PaginaPanel';
import { fechaCorta } from '../../ui/format';

const ESTADO: Record<EstadoDeReclamo, { tono: TonoInsignia; texto: string }> = {
  abierto: { tono: 'pendiente', texto: 'Abierto' },
  en_proceso: { tono: 'info', texto: 'En proceso' },
  cerrado: { tono: 'confirmado', texto: 'Resuelto' },
};
const TIPO: Record<TipoDeReclamo, string> = { atencion: 'Atención', cobro: 'Cobro o seña', plataforma: 'Plataforma', otro: 'Otro' };

type Pestana = 'abiertos' | 'resueltos';

export function Reclamos() {
  const pedido = usePedido(() => administracion.reclamos(), []);
  const [pestana, setPestana] = useState<Pestana>('abiertos');
  const todos = pedido.datos ?? [];
  const abiertos = todos.filter((r) => r.estado !== 'cerrado');
  const resueltos = todos.filter((r) => r.estado === 'cerrado');
  const lista = pestana === 'abiertos' ? abiertos : resueltos;

  return (
    <SeccionPanel titulo="Reclamos" descripcion="Lo que reportan los pacientes sobre turnos, cobros y la plataforma">
      {pedido.estado === 'cargando' && <Cargando />}
      {pedido.estado === 'error' && <FalloDeCarga mensaje={pedido.error ?? ''} alReintentar={pedido.recargar} />}
      {pedido.datos && (
        <Tarjeta>
          <Pestanas<Pestana> etiqueta="Reclamos" activa={pestana} alElegir={setPestana} pestanas={[
            { id: 'abiertos', texto: 'Sin resolver', cantidad: abiertos.length },
            { id: 'resueltos', texto: 'Resueltos', cantidad: resueltos.length },
          ]} />
          <div className="listado">
            {lista.length === 0 ? (
              <EstadoVacio icono="flag" titulo={pestana === 'abiertos' ? 'No hay reclamos abiertos' : 'Todavía no hay reclamos resueltos'} />
            ) : lista.map((r) => (
              <button key={r.id_reclamo} type="button" className="fila-turno" onClick={() => irA(`/admin/reclamo/${r.id_reclamo}`)}>
                <span className="fila-turno__hora">{fechaCorta(r.fecha_apertura.slice(0, 10))}</span>
                <span className="fila-turno__datos">
                  <strong>{r.asunto}</strong>
                  <span>{r.paciente.nombre}{r.profesional ? ` sobre ${r.profesional.nombre}` : ''} · {TIPO[r.tipo]}</span>
                </span>
                <Insignia tono={ESTADO[r.estado].tono} tamano="chico">{ESTADO[r.estado].texto}</Insignia>
                <Icono nombre="chevron-right" tamano={17} />
              </button>
            ))}
          </div>
        </Tarjeta>
      )}
    </SeccionPanel>
  );
}

export function DetalleReclamo({ params }: ScreenProps) {
  const id = Number(params['id']);
  const pedido = usePedido(() => administracion.reclamo(id), [id]);
  useTitulo(pedido.datos && `Reclamo: ${pedido.datos.asunto}`);
  const accionVolver = <Boton tamano="chico" variante="secundario" icono="arrow-left" onClick={() => volver('/admin/reclamos')}>Volver</Boton>;
  if (!pedido.datos) {
    return (
      <SeccionPanel titulo="Reclamo" acciones={accionVolver}>
        {pedido.estado === 'cargando'
          ? <Cargando />
          : <FalloDeCarga mensaje={pedido.error ?? 'No encontramos el reclamo'} alReintentar={pedido.recargar} />}
      </SeccionPanel>
    );
  }
  const r = pedido.datos;
  return (
    <SeccionPanel titulo={r.asunto} acciones={accionVolver}
      descripcion={`${TIPO[r.tipo]} · abierto el ${fechaCorta(r.fecha_apertura.slice(0, 10))}`}>
      <div className="dos-columnas">
        <div className="pila">
          <Tarjeta>
            <header className="tarjeta-titulo">
              <h3>Qué reportó el paciente</h3>
              <Insignia tono={ESTADO[r.estado].tono}>{ESTADO[r.estado].texto}</Insignia>
            </header>
            <p className="texto-resena">{r.descripcion}</p>
          </Tarjeta>
          {r.resena && <ResenaDelTurno reclamo={r} alCambiar={pedido.recargar} />}
          <Gestion reclamo={r} alCambiar={pedido.recargar} />
        </div>
        <Tarjeta>
          <h3 className="titulo-tarjeta">Involucrados</h3>
          <dl className="datos-modal">
            <div><dt>Paciente</dt><dd>{r.paciente.nombre}</dd></div>
            <div><dt>Correo</dt><dd>{r.paciente.email}</dd></div>
            {r.profesional && (
              <div><dt>Profesional</dt><dd><Enlace a={`/admin/profesional/${r.profesional.id_profesional}`}>{r.profesional.nombre}</Enlace></dd></div>
            )}
            {r.turno && <div><dt>Turno</dt><dd><span className="codigo">{r.turno.codigo}</span> · {fechaCorta(r.turno.fecha)}</dd></div>}
            {r.responsable && <div><dt>Responsable</dt><dd>{r.responsable}</dd></div>}
          </dl>
        </Tarjeta>
      </div>
    </SeccionPanel>
  );
}

/** Administrador.eliminarResena desde el reclamo, cuando lo que se reclama es una reseña que viola las políticas. */
function ResenaDelTurno({ reclamo: r, alCambiar }: { reclamo: ReclamoAdministrado; alCambiar: () => void }) {
  const { mostrarAviso } = useAvisos();
  const [ocupado, setOcupado] = useState(false);
  if (!r.resena) return null;
  const { id_resena, calificacion, comentario, visible } = r.resena;

  const cambiar = async () => {
    setOcupado(true);
    try {
      await administracion.moderarResena(id_resena, !visible);
      mostrarAviso({ tono: 'exito', titulo: visible ? 'Reseña dada de baja' : 'Reseña publicada de nuevo' });
      alCambiar();
    } catch (e) {
      mostrarAviso({ tono: 'error', titulo: 'No pudimos cambiar la reseña', cuerpo: e instanceof ApiError ? e.message : undefined });
    } finally {
      setOcupado(false);
    }
  };

  return (
    <Tarjeta>
      <header className="tarjeta-titulo">
        <h3>Reseña del turno</h3>
        {!visible && <Insignia tono="cancelado" tamano="chico">Oculta</Insignia>}
      </header>
      <Estrellas puntaje={calificacion} tamano={16} />
      {comentario && <p className="texto-resena">{comentario}</p>}
      <p className="nota">
        {visible
          ? 'Si viola las políticas de la plataforma, dala de baja: deja de verse en el perfil y en el promedio del profesional.'
          : 'No se ve en el perfil ni cuenta para el promedio del profesional.'}
      </p>
      <div className="acciones-turno">
        <Boton variante={visible ? 'peligro' : 'secundario'} cargando={ocupado} onClick={() => void cambiar()}>
          {visible ? 'Dar de baja la reseña' : 'Volver a publicarla'}
        </Boton>
      </div>
    </Tarjeta>
  );
}

function Gestion({ reclamo: r, alCambiar }: { reclamo: ReclamoAdministrado; alCambiar: () => void }) {
  const { mostrarAviso } = useAvisos();
  const [resolucion, setResolucion] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const ejecutar = async (accion: () => Promise<unknown>, exito: string) => {
    setOcupado(true);
    setError(null);
    try {
      await accion();
      mostrarAviso({ tono: 'exito', titulo: exito });
      alCambiar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos actualizar el reclamo. Probá de nuevo.');
    } finally {
      setOcupado(false);
    }
  };

  if (r.estado === 'cerrado') {
    return (
      <Tarjeta>
        <h3 className="titulo-tarjeta">Resolución</h3>
        <p className="texto-resena">{r.resolucion}</p>
        <p className="nota">Cerrado el {fechaCorta((r.fecha_cierre ?? '').slice(0, 10))} por {r.responsable}.</p>
        <AvisoError>{error}</AvisoError>
        <div className="acciones-turno">
          <Boton variante="secundario" cargando={ocupado}
            onClick={() => void ejecutar(() => administracion.tomarReclamo(r.id_reclamo), 'Reclamo reabierto')}>Reabrir</Boton>
        </div>
      </Tarjeta>
    );
  }

  return (
    <Tarjeta>
      <h3 className="titulo-tarjeta">Resolución</h3>
      <div className="formulario">
        <AreaTexto etiqueta="Qué se hizo" rows={4} maxLength={1000} value={resolucion}
          placeholder="Se le devolvió la seña al paciente y se habló con el profesional." onChange={(e) => setResolucion(e.target.value)} />
        <p className="nota">Al cerrarlo, le mandamos la resolución por email a {r.paciente.nombre}, que también la ve en Mi cuenta.</p>
        <AvisoError>{error}</AvisoError>
        <div className="acciones-turno">
          {r.estado === 'abierto' && (
            <Boton variante="secundario" disabled={ocupado}
              onClick={() => void ejecutar(() => administracion.tomarReclamo(r.id_reclamo), 'Reclamo en proceso')}>
              Marcar en proceso
            </Boton>
          )}
          <Boton disabled={resolucion.trim().length < 10} cargando={ocupado}
            onClick={() => void ejecutar(() => administracion.cerrarReclamo(r.id_reclamo, resolucion), 'Reclamo cerrado')}>
            Cerrar reclamo
          </Boton>
        </div>
      </div>
    </Tarjeta>
  );
}
